import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import dayjs from "dayjs";
import "dayjs/locale/fr";

const DEFAULT_LANGUAGE = "en";

const getInitialLanguage = (): string => {
  if (typeof window === "undefined") {
    return DEFAULT_LANGUAGE;
  }

  const storedLanguage = localStorage.getItem("i18nextLng");
  if (storedLanguage) {
    return storedLanguage;
  }

  const browserLanguage = navigator.language?.split("-")?.[0];
  if (browserLanguage && ["en", "fr"].includes(browserLanguage)) {
    return browserLanguage;
  }

  return DEFAULT_LANGUAGE;
};

const resources = {
  en: {
    translation: {
      common: {
        language: "Language",
        english: "English",
        french: "Français",
        manage: "Manage",
        viewAll: "View All",
        complete: "Complete",
        goBack: "Go back",
        continue: "Continue",
        dashboard: "Dashboard",
        learnMore: "Learn more",
        year: "year",
        hiWithName:
          "Hi {{name}}, here's everything you need to manage your Parachute Partners account.",
        hiFallbackName: "there",
        monthNamesShort: [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ],
      },
      nav: {
        home: "Home",
        organization: "Organization",
        subscription: "Subscription",
        benefits: "Benefits",
        team: "Team",
        signOut: "Sign out",
      },
      setup: {
        finishSetup: "Finish setting up your account:",
      },
      todos: {
        addBillingTitle: "Add your billing information",
        inviteMemberTitle: "Invite your first team member!",
        descriptionPlaceholder: "",
      },
      hero: {
        teamMembers: "Team Members",
        onboardedMembers: "Onboarded Members",
        onboardedRate: "Onboarded Employee Rate",
        totalRewards: "Total Rewards All-Time",
      },
      lifestyle: {
        title: "Lifestyle Account",
        manage: "Manage",
        noTeamMembersTitle: "No Team Members",
        noTeamMembersSubtitle: "Invite your team to collaborate on this project.",
        inviteMembers: "Invite Members",
        noGroupTitle: "No Lifestyle Group",
        noGroupSubtitle: "Set up a group for your members.",
        createGroup: "Create Lifestyle Group",
        noBenefitsTitle: "No Lifestyle Benefits set up yet",
        noBenefitsSubtitle: "Help your team with their wellness expenses",
        createBenefits: "Create my Lifestyle Benefits Account",
        totalRequested: "Total Requested",
        employeeParticipation: "Employee Participation",
        avgPerUser: "Average per User",
        recentPending: "Recent Pending Transactions",
        noPending: "No pending transactions right now.",
        pendingUser: "Pending user",
      },
      benefits: {
        title: "Benefits",
      },
      team: {
        title: "Team",
        teamMembers: "Team Members",
        onboardedMembers: "Onboarded Members",
        onboardedRate: "Onboarded Employee Rate",
        onboardingProgress: "Onboarding Progress",
        allOnboarded: "All users onboarded",
        noMembersTitle: "No team members yet",
        noMembersSubtitle: "Invite your employees to get started with Parachute.",
        inviteMembers: "Invite Members",
        onboardedSummary: "{{onboarded}} of {{total}} onboarded",
      },
      rewards: {
        title: "Rewards Performance",
        monthlyTrend: "Monthly Savings Trend",
        xAxis: "Month",
        yAxis: "Savings ($)",
        annualSavings: "Annual Team Savings",
        monthlySavings: "Monthly Team Savings",
        boostOn: "Boost - On",
        boostOff: "Boost - Off",
        tooltipLabel: "Savings",
        noData: "No rewards performance data available yet",
        noDataSubtitle: "Reward insights will appear once there are rewards to analyze",
      },
      airdrops: {
        title: "Recent Airdrops",
        viewAll: "View All",
        none: "No airdrops sent yet",
        noneCta: "Send your first airdrop",
        noneSubtitle: "to reward your team members",
      },
      airdrop: {
        common: {
          header: "Airdrops",
          subheader: "Send Gift Cards to your employees",
          breadcrumbs: {
            benefits: "Benefits",
            airdrops: "Airdrops",
            selectMembers: "Select Members",
            selectGiftCard: "Select Gift Card",
            selectAmount: "Select Amount",
            confirm: "Confirm",
          },
          other: "Other",
        },
        hero: {
          kicker: "Airdrop gift cards",
          title: "Recognize effort instantly.",
          description:
            "Send gift card rewards directly to your employees, right inside the Parachute app. Choose from 100+ brands. Airdrops make appreciation effortless and meaningful.",
          cta: "Airdrop a Gift Card",
          alt: "Gift card rewards illustration",
        },
        summaryCards: {
          totalAirdropsSent: "Total Airdrops Sent",
          totalValueSent: "Total Value Sent",
        },
        history: {
          title: "Your Airdrop History",
          searchPlaceholder: "Search by name or brand",
          loading: "Loading airdrop history...",
          errorTitle: "Error loading airdrop history",
          errorSubtitle: "Please try again later",
          tableHeaders: {
            fullName: "Full Name",
            date: "Date",
            brand: "Brand",
            totalSaleAmount: "Total Sale Amount",
            receipt: "Receipt",
          },
          receipt: {
            view: "View",
            none: "No receipt",
          },
          emptySearch: "No airdrops match your search.",
          emptyHistory: "You have not sent any Airdrops to your employees yet.",
        },
        selectMembers: {
          title: "Who are you sending it to?",
          searchPlaceholder: "Search by name or email",
          tableHeaders: {
            select: "Select",
            name: "Name",
            email: "Email",
            workAnniversary: "Work Anniversary",
            birthday: "Birthday",
            status: "Status",
          },
          status: {
            inProgress: "In Progress",
            onboarded: "Onboarded",
          },
          ariaSelect: "Select {{email}}",
          emptySearch: "No team members match your search.",
          emptyState: "You currently have no onboarded team members.",
        },
        selectOffers: {
          title: "Which reward do you want to Airdrop?",
          searchPlaceholder: "Search by offer name",
          loading: "Gift card offers are currently loading...",
        },
        selectAmount: {
          title: "Which amount?",
          presetLabel: "Pre-set amounts",
          customLabel: "Or enter a custom amount",
          customPlaceholder: "Enter an Amount",
          messageLabel: "Add a short message",
          messagePlaceholder: "100 characters maximum",
          requireOffer: "Please choose a gift card before selecting the amount.",
        },
        confirm: {
          title: "Confirm your Airdrop",
          summary:
            "You are sending a {{amount}} {{brand}} Gift Card to {{name}}",
          messageIncluded: '"{{message}}"',
          messageMissing: "(no message included)",
          paymentLine:
            "Email: {{email}} | Payment: {{cardBrand}} ending in {{last4}}",
          notification:
            "{{name}} will receive a push and email notification (if enabled). Instructions on how to use the gift card will be provided.",
          modal: {
            title: "Confirm Airdrop",
            description:
              "Please confirm your {{amount}} {{brand}} gift card for {{name}}.",
            confirm: "Send it!",
          },
          sendAirdrop: "Send Airdrop",
        },
        todoCard: {
          title: "Send an Airdrop to a Team Member!",
        },
        complete: {
          breadcrumbs: {
            complete: "Complete",
          },
          successTitle: "Congratulations, your gift card has just been sent!",
          sendAnother: "Send another Airdrop",
          returnHome: "Return Home",
        },
      },
      auth: {
        common: {
          backToHome: "Back to home",
          backToSignIn: "Back to Sign In",
          signIn: "Sign in",
          signInCta: "Sign In",
          signUp: "Create your account",
          or: "Or",
          forgotPassword: "Forgot password?",
          continueWithGoogle: "Continue with Google",
          devBanner: "DEV VERSION",
          betaBanner: "BETA VERSION",
        },
        signup: {
          title: "Create your account",
          subtitlePrompt: "Already have an account?",
          signInLink: "Sign in",
          firstName: "First name",
          lastName: "Last name",
          email: "Email address",
          password: "Password",
          confirmPassword: "Confirm password",
          submit: "Create account",
          continueWithGoogle:  "Continue with Google",
          devBanner: "DEV VERSION",
          betaBanner: "BETA VERSION",
        },
        signin: {
          title: "Sign in to your account",
          subtitlePrompt: "Don't have an account?",
          signUpLink: "Create an account",
          email: "Email address",
          password: "Password",
          rememberMe: "Remember me",
          submit: "Sign In",
        },
        forgotPassword: {
          title: "Forgot your password?",
          subtitle:
            "Enter your email and we'll send you instructions to reset your password.",
          emailPlaceholder: "Email address",
          submit: "Send reset link",
          backToSignIn: "Back to Sign In",
        },
        resetPassword: {
          title: "Reset Password",
          subtitle: "Enter your new password below.",
          newPassword: "New password",
          confirmPassword: "Confirm new password",
          submit: "Update password",
          successTitle: "Password reset",
          successDescription:
            "Your password has been reset. You may now login to the dashboard.",
          backToSignIn: "Back to Sign In",
        },
        forceResetPassword: {
          title: "Choose a Password",
          subtitle: "Please choose a new password to continue.",
          newPassword: "New password",
          confirmPassword: "Confirm new password",
          submit: "Update password",
          successTitle: "Success!",
          successDescription:
            "Your password has been changed. You may now login to the dashboard.",
          backToSignIn: "Back to Sign In",
        },
        pending: {
          title: "Account pending approval",
          description:
            "Your account is still under review. We’ll send you an email when you're approved.",
          backToHome: "Back to home",
        },
        confirmEmail: {
          title: "Check your email",
          instructions:
            "We've sent you an email with a link to confirm your Parachute Partners account.",
          noEmail: "Didn't receive an email?",
          resend: "Resend Confirmation",
        },
        requestSent: {
          title: "Request Pending",
          description:
            "Your request to join Parachute Partners was sent to our Partnership Team. We will notify you by email once it has been approved.",
          questions: "Questions?",
          sendEmail: "Send us an email!",
        },
        accountConfirmed: {
          successTitle: "Account verification successful!",
          successDescription:
            "Your account has been verified. You can now log in and start your Parachute experience.",
          expiredTitle: "Account verification expired",
          expiredDescription:
            "The verification email you used has expired. This may be because you have already verified your account or because the verification link has expired. Please request a new verification email.",
          invalidTitle: "Invalid verification link",
          invalidDescription:
            "The verification link you used is invalid. Please use the latest verification link you received or request a new verification email.",
          alreadyTitle: "Account verification already completed",
          alreadyDescription:
            "Your account has already been verified. Please sign in to continue. If you believe this is an error, please contact support.",
          requestNew: "Request New Verification Email",
        },
        userConfirmed: {
          title: "Your user account has been confirmed!",
          description:
            "You can now return to the app and log in to start your Parachute experience.",
        },
        welcome: {
          title: "Let's set up your profile",
          subtitle:
            "Tell us a bit about your organisation so we can configure your account.",
          organization: "Organization name",
          website: "Your website",
          phoneNumber: "Phone number",
          description: "Describe your organization",
          submit: "Create my account",
        },
      },
      cashbackRewards: {
        header: "Cashback Rewards",
        subheader: "Manage your rewards program here",
        breadcrumbs: {
          benefits: "Benefits",
          cashback: "Cashback Rewards",
        },
        hero: {
          kicker: "Cashback rewards program",
          title: "Boost every purchase with employer-funded cashback.",
          description:
            "Set a company-funded boost so your team earns richer rewards when making purchases via the Parachute app. Choose the boost percent, cap it per employee, and adjust anytime to align with your rewards budget.",
          alt: "Cashback rewards illustration",
        },
        boostSection: {
          title: "Boost Rewards",
          boostOffersBy: "Boost offers by",
          amountPlaceholder: "Amount",
          boostLimitLabel: "Set Boost Limit  $",
          boostLimitPlaceholder: "Amount",
          helperBoost:
            "Activating this feature will enable your team to earn higher rewards when they shop with partner's brands (billed monthly to your organisation).",
          helperLimit: "Set a yearly maximum boost amount per employee.",
          saving: "Saving...",
          confirm: "Confirm Changes",
        },
      },
      groupInsurance: {
        header: "Group Insurance",
        subheader: "Manage your group insurance options here",
        hero: {
          kicker: "Coming Soon!",
          title: "Protect your team with flexible group coverage.",
          description:
            "Give your employees the security they deserve. Easily set up and manage health, dental, and disability coverage, and adjust plans anytime as your organization grows.",
          notAvailable: "This feature is not available yet. Stay tuned!",
          alt: "Group insurance illustration",
        },
      },
      lifestyleCategories: {
        header: "Lifestyle Account",
        subheader: "Automate reimbursements on select expenses",
        breadcrumbs: {
          benefits: "Benefits",
          lifestyle: "Lifestyle Account",
          createGroup: "Create Group",
        },
        step1Title: "Name Your Lifestyle Group",
        step1Description:
          "Give your lifestyle benefits group a name that your employees will recognize.",
        groupNameLabel: "Lifestyle Group Name",
        groupNamePlaceholder: "e.g. Employee Wellness Benefits",
        groupNameHelper: "This name will be visible to your employees",
        step2Title: "Select Categories",
        step2Description:
          "Choose which lifestyle expense categories you want to include in your employee benefits group (you can change it later).",
        selectAll: "Select All",
        loading: "Loading categories...",
        examplesTitle: "Examples",
        noExamples: "No examples available for this category.",
      },
      lifestyleRules: {
        header: "Lifestyle Account",
        subheader: "Automate reimbursements on select expenses",
        breadcrumbs: {
          benefits: "Benefits",
          lifestyle: "Lifestyle Account",
          rules: "Rules",
        },
        stepTitle: "Create Your Rules",
        stepDescription: "Set up the rules and limits for your lifestyle benefits.",
        generalRules: "General Rules",
        yearlyAmountLabel: "Yearly Amount per Employee",
        yearlyAmountHelper: "Amount employees will have per year",
        coverageLabel: "General Coverage",
        coverageHelper: "Percentage of bills that will be reimbursed",
        allocationTitle: "Allocation Choice",
        allocationSubtitle: "Choose how the yearly amount is distributed to employees",
        allocationOptions: {
          proRataTitle: "Pro-rata System",
          proRataDescription:
            "Employees receive a proportional amount based on when the program is activated. For example, if activated mid-year, employees get 50% of the yearly amount.",
          fullAmountTitle: "Full Amount",
          fullAmountDescription:
            "Employees receive the full yearly amount regardless of when the program is activated.",
        },
      },
      lifestyleConfirm: {
        header: "Lifestyle Account",
        subheader: "Automate reimbursements on select expenses",
        breadcrumbs: {
          benefits: "Benefits",
          lifestyle: "Lifestyle Account",
          confirm: "Confirm",
        },
        title: "Review and Confirm",
        description:
          "Review your lifestyle benefits settings before finalizing.",
        groupName: "Group Name",
        categories: "Selected Categories",
        rules: "Rules",
        yearlyAmount: "Yearly Amount per Employee",
        coverage: "General Coverage",
        allocation: "Allocation Choice",
      },
      brands: {
        title: "Popular Brands",
        purchase: "purchase",
        purchases: "purchases",
        noData: "No popular brands data available yet",
        noDataSubtitle: "Brands will appear here once users start making purchases",
      },
      perks: {
        header: "Benefits Account",
        subheader: "Create and manage your benefits account here",
        hero: {
          kicker: "Support your team in ways that matter",
          title: "Elevate your benefits experience.",
          description:
            "Empower your organization with flexible, useful perks. From cash-back rewards to lifestyle allowances, it's all managed effortlessly with Parachute.",
        },
        sections: {
          active: "Active",
          addMore: "Add these benefits for your team",
        },
        status: {
          active: "Active",
          beta: "Beta",
          comingSoon: "Coming soon",
        },
        itemDetails: {
          waitlistCta: "Get early access by joining our waitlist!",
          featureActive: "Feature Active",
          joinWaitlist: "Join waitlist",
          alreadyIncluded: "Already included",
          activate: "Activate {{name}}",
          deactivate: "Deactivate {{name}}",
          close: "Close",
        },
        items: {
          perk_airdrop: {
            title: "Airdrops",
            fullTitle: "Airdrops",
            description: "Airdrop digital gift cards to your team - and more",
          },
          perk_allCashback: {
            title: "Rewards",
            fullTitle: "Cashback Rewards",
            description: "Earn when you shop at 1,000+ brands",
          },
          perk_onTheGo: {
            title: "Lifestyle",
            fullTitle: "Lifestyle",
            description: "Automate reimbursement of selected expenses",
          },
          perk_celebrations: {
            title: "Celebrations",
            fullTitle: "Celebrations",
            description: "Automate celebrations (birthday, holidays, etc.)",
          },
          perk_slack: {
            title: "Recognition",
            fullTitle: "Recognition",
            description: "Let your team reward each other for epic wins!",
          },
          perk_24_7: {
            title: "24/7 Pay",
            fullTitle: "24/7 Pay",
            description: "Access your pay when you need it most",
          },
          perk_customerRewards: {
            title: "Customer Rewards",
            fullTitle: "Customer Rewards",
            description: "Send gift cards to your customers",
          },
        },
      },
      perksSection: {
        groupInsurance: {
          title: "Group Insurance",
          subtitle: "Protect your team. Let Parachute shop for the best insurance.",
          statusProtected: "Protected",
          statusNotProtected: "Not protected",
          expiresOn: "Expires on {{date}}",
          noExpiry: "N/A",
          alt: "Protected coverage icon",
        },
        myBenefits: "My Parachute Benefits",
      },
      teamPage: {
        header: "Team",
        subheader: "Manage your Parachute invites here",
        addressAlert: "🚨 First go to the Organization page and add your address!",
        addCardAlert:
          "🚨 Click here to go to the Subscription page and add a credit card!",
        membersCount: "{{count}} members",
        searchPlaceholder: "Search by name or email",
        addMember: "+ Add Team Member",
        tableHeaders: {
          name: "Name",
          email: "Email",
          workAnniversary: "Work Anniversary",
          birthday: "Birthday",
          status: "Status",
          actions: "Actions",
        },
        status: {
          inProgress: "In Progress",
          onboarded: "Onboarded",
          readyToImport: "Ready to import",
          needsReview: "Needs review",
        },
        emptySearch: "No team members match your search.",
        emptyTitle: "It's time to invite your first team member.",
        emptyDescription:
          "Start building your team by adding your first employee. They'll receive an invitation to join your Parachute program.",
        pagination: {
          showing: "Showing",
          to: "to",
          of: "of",
          previous: "Previous",
          next: "Next",
        },
        modals: {
          addTitle: "Add a Team Member",
          editTitle: "Edit a Team Member",
          deleteTitle: "Remove User",
          deleteMessage: "Remove {{name}} from the team?",
          resendTitle: "Resend Invitation",
          resendMessage: "Resend a Parachute invitation email to {{name}}?",
          resendSuccessTitle: "Invitation sent",
          resendSuccessMessage: "Invitation email sent to {{name}}.",
          importPreviewTitle: "Team Import Preview",
          importLoadingTitle: "Importing team members...",
          importLoadingStatus: "{{completed}} of {{total}}",
          importLoadingPreparing: "Preparing import...",
          importPreviewLoading: "Loading preview...",
          importPreviewEmpty: "No rows found in this file.",
          importPreviewTotals: {
            usersReady: "Users ready to import",
            invalidEntries: "Invalid entries",
            importingOnlyValid: "Importing will only add the valid users.",
          },
          importActions: {
            cancel: "Cancel",
            import: "Import",
            importing: "Importing...",
          },
          labels: {
            firstName: "First name",
            lastName: "Last name",
            email: "Email address",
            emailLanguage: "Email language",
            languageEnglish: "English",
            languageFrench: "French",
            workAnniversary: "Work Anniversary (Optional)",
            birthday: "Birthday (Optional)",
            selectDate: "Select date",
            autoSignUp: "Auto-signup",
            autoSignUpHelper: "Automatically create the user's mobile account and send them the password via email. No activation is required.",
            sendInvite: "Send Invite",
            updateMember: "Update Team Member",
            confirm: "Confirm",
            ok: "Ok",
          },
        },
        import: {
          importTitle: "Import",
          menuLabel: "Import options",
          importFile: "Import Excel or CSV…",
          downloadTemplate: "Download CSV template",
          downloadTemplateXlsx: "Download Excel template",
        },
        actions: {
          edit: "Edit",
          remove: "Remove",
          clearSearch: "Clear search",
        },
        importPreview: {
          statusReady: "Ready to import",
          statusNeedsReview: "Needs review",
        },
        errors: {
          genericImportTitle: "Import Problem",
          genericImportSubtitle:
            "The import file appears to be improperly formatted. Please download a template (either Excel or CSV format):",
          emailRequired: "Email is required",
          emailInvalid: "Invalid email address",
          firstNameRequired: "First name is required",
          lastNameRequired: "Last name is required",
          emailAlreadyAdded: "This email address has already been added as a team member!",
          csvEmpty: "The selected file is empty.",
          csvNoData: "No data rows were found in the file.",
          missingColumns: "Missing required column(s): {{columns}}",
          missingPayment: "Add a payment method before importing team members.",
          noRowsToImport: "No rows found to import.",
          noValidRows: "No valid rows found to import. Review the highlighted issues.",
          csvReadError:
            "We couldn't read that file. Please verify the format and try again.",
          noValidSelected: "No valid rows selected for import.",
          userAlreadyOnTeam: "User is already on your team.",
          duplicateInFile: "{{email}} appears multiple times in this file.",
          missingNameForEmail: "Missing first or last name for {{email}}.",
          missingEmail: "Missing email address.",
          unsupportedFile: "Unsupported file type. Please upload a CSV or Excel file.",
        },
        toasts: {
          importCancelled:
            "Import cancelled after importing {{count}} of {{total}} team member(s).",
          importSuccess: "Imported {{count}} team member(s).",
          importSkippedSummary:
            "{{count}} row(s) skipped. {{summary}}{{remaining}}",
          rowsSkippedRemaining: " ...and {{remaining}} more.",
          rowAlreadyOnTeam: "Row {{line}}: {{email}} is already on your team.",
          rowImportFailed: "Row {{line}}: unable to import {{email}}.",
        },
        importResults: {
          successBoxTitle: "Successful Imports",
          failureBoxTitle: "Failed Imports",
          exportFailures: "Download error report",
          exportDescription:
            "Generates a new {{ext}} file that contains only the users that failed to import.",
        },
      },
      lifestyleMain: {
        header: "Lifestyle Account",
        subheader: "Automate reimbursements on select expenses",
        breadcrumbs: {
          benefits: "Benefits",
          lifestyleAccount: "Lifestyle Account",
        },
        setup: {
          title: "Empower Your Team",
          subtitle: "Lifestyle Benefits That Work",
          description:
            "Create a custom lifestyle account that automatically reimburses your employees for their everyday expenses. Boost morale, attract top talent, and show you care about their work-life balance.",
          cta: "Create Lifestyle Account",
          cards: {
            automaticReimbursements: {
              title: "Automatic Reimbursements",
              description:
                "Set up rules and let the system handle reimbursements automatically. No more manual expense reports.",
            },
            customCategories: {
              title: "Custom Categories",
              description:
                "Choose from 15+ lifestyle categories or create your own. Tailor benefits to your team's needs.",
            },
            insights: {
              title: "Insights & Analytics",
              description:
                "Track spending patterns, popular categories, and measure the impact of your lifestyle benefits.",
            },
          },
        },
        loading: {
          title: "Loading Analytics",
          description: "Fetching your lifestyle benefits analytics data...",
        },
        error: {
          title: "Error Loading Data",
          description:
            "There was an issue loading your analytics data. Please try again.",
          retry: "Retry Loading",
        },
        filters: {
          groupLabel: "Group:",
          periodLabel: "Period:",
          manage: "Manage",
          groupOptionLabel: "{{name}} ({{count}} member)",
          groupOptionLabel_plural: "{{name}} ({{count}} members)",
          groupOptionComingSoon: "Create another lifestyle group (Coming Soon)",
          timeRanges: {
            last7Days: "Last 7 days",
            last30Days: "Last 30 days",
            yearToDate: "Year to Date",
            last12Months: "Last 12 months",
          },
        },
        summary: {
          title: "Summary",
          yearlyBudget: "Yearly Budget per User (Maximum)",
          coverageRate: "Coverage Rate",
          coveredCategories: "Covered Categories",
          categoriesCount: "({{count}})",
          empty: "No claims made for this group yet.",
        },
        covered: {
          title: "Who's Covered",
          membersCount: "({{count}} member)",
          membersCount_plural: "({{count}} members)",
          fallbackName: "Member",
          tableHeaders: {
            name: "Name",
            email: "Email",
          },
          noSearchResults: "No team members match your search.",
          noMembersTitle: "No Team Members",
          noMembersSubtitle: "Invite your team to collaborate on this project.",
          invite: "Invite Members",
        },
        performance: {
          title: "Performance",
          totalRequested: "Total Requested",
          averagePerMember: "Average per Member",
          participationRate: "Participation Rate",
        },
        pending: {
          title: "Pending Transactions",
          emptyTitle: "No pending transactions",
          emptySubtitle:
            "Transactions will appear here when employees submit expenses",
        },
        history: {
          title: "Transaction History",
          emptyTitle: "No history for selected time period",
          emptySubtitle: "Completed or rejected transactions will appear here",
          statusFallback: "Completed",
        },
        categories: {
          popularTitle: "Most Popular Categories",
          popularDescription:
            "Total spend per category for {{timeRange}}",
          timeRanges: {
            last7Days: "the last 7 days",
            last30Days: "the last 30 days",
            yearToDate: "year to date",
            last12Months: "the last 12 months",
          },
          avgReimbursement: "Avg Reimbursement per Claim",
          spendingByCategoryTitle: "Spending by Category",
          topLabel: "(Top 5)",
          other: "Other",
        },
      },
      lifestyleManageGroup: {
        header: "Manage Group",
        subheader: "Edit your lifestyle account settings and manage member benefits",
        breadcrumbs: {
          benefits: "Benefits",
          lifestyleAccount: "Lifestyle Account",
          manageGroup: "Manage Group",
        },
        loading: {
          subheader: "Loading group details...",
        },
        error: {
          subheader: "Error loading group details",
          title: "Error Loading Group",
          description: "Error loading group details",
        },
        notFound: {
          subheader: "Group not found",
          title: "Group Not Found",
          description: "The requested group could not be found.",
        },
        form: {
          title: "Basic Information",
          fields: {
            groupName: "Group Name",
            yearlySpendingLimit: "Yearly Spending Limit",
            coveragePercentage: "Coverage Percentage",
            resetDate: "Reset Date",
            runOffPeriod: "Run-off Period (days)",
            prorated: "Enable prorated benefits for new members",
          },
          placeholders: {
            groupName: "Enter group name",
            yearlySpendingLimit: "2500",
            coveragePercentage: "80",
            runOffPeriod: "30",
            deleteConfirmation: "Type group name here",
          },
          validation: {
            groupNameRequired: "Group name is required",
            groupNameMinLength: "Group name must be at least 3 characters",
            yearlySpendingLimit: "Yearly spending limit must be greater than 0",
            coveragePercentage: "Coverage percentage must be between 0 and 100",
            runOffPeriod: "Run-off period must be between 0 and 365 days",
            noGroupSelected: "No group selected",
            fixValidation: "Please fix validation errors before saving",
          },
        },
        categories: {
          title: "Eligible Categories",
          description: "Choose which categories qualify for claims ({{count}} selected)",
          description_plural: "Choose which categories qualify for claims ({{count}} selected)",
          infoTitle: "Examples",
          noExamples: "No examples available for this category.",
          categoryDetails: {
            "home-daily-life": {
              name: "Home & Daily Life",
              description:
                "Home and daily life services and memberships (no office gear)",
              examples: [
                "House cleaning services",
                "Laundry/dry cleaning pickup",
                "Grocery delivery memberships (e.g., Instacart+)",
                "Meal kit subscriptions (e.g., Goodfood, HelloFresh)",
                "Smart home devices (e.g., lighting, speakers)",
              ],
            },
            "remote-work-productivity": {
              name: "Remote Work & Productivity",
              description:
                "Remote work expenses, home office furniture and productivity tools",
              examples: [
                "Co-working space fees",
                "Internet upgrades / partial reimbursements",
                "Tech accessories (mouse, webcam, keyboard)",
                "Standing desks, ergonomic tools",
                "Home office furniture (chair, desk, monitor stand)",
                "Blue light glasses",
              ],
            },
            "transportation-eco-mobility": {
              name: "Transportation & Eco-Mobility",
              description: "Eco-friendly and convenience transportation",
              examples: [
                "Public transit passes",
                "Bike share memberships",
                "E-bike or scooter purchase subsidy",
                "Car share memberships (e.g., Communauto, Zipcar)",
                "Car washes and detailing",
                "Winter tire change / storage",
              ],
            },
            "culture-live-events": {
              name: "Culture & Live Events",
              description: "Cultural experiences (museums, galleries, theatre, concerts)",
              examples: [
                "Museum or gallery passes",
                "Theatre or concert passes",
                "Festival tickets (music, arts, cultural)",
                "Comedy shows",
              ],
            },
            "entertainment-subscriptions": {
              name: "Entertainment & Media",
              description: "Entertainment streaming, media and gaming",
              examples: [
                "Netflix, Disney+, Spotify",
                "Movie theatre passes",
                "Gaming subscriptions (Xbox, Playstation, Nintendo)",
                "Audiobooks & podcasts (Audible, Wondery+)",
              ],
            },
            "learning-personal-growth": {
              name: "Learning & Personal Growth",
              description: "Courses, books and professional growth",
              examples: [
                "Courses (LinkedIn Learning, Coursera, Udemy)",
                "Creative class subscriptions (Masterclass, Skillshare)",
                "Language learning apps (Duolingo Plus, Babbel)",
                "Books and eBooks",
                "Conference tickets",
                "Career coaching",
                "Financial literacy workshops",
                "Public speaking classes",
              ],
            },
            "hobbies-creative": {
              name: "Hobbies & Creative Supplies",
              description: "Supplies and materials for creative hobbies and DIY",
              examples: [
                "Art supplies (paints, brushes, canvases)",
                "Craft materials (yarn, fabric, beads)",
                "DIY maker tools (non-professional)",
                "Model kits (scale models, LEGO kits)",
                "Photography accessories (tripods, filters, lighting)",
                "Musical instrument accessories",
              ],
            },
            "wellness-self-care": {
              name: "Wellness & Self-Care",
              description: "Personal wellness and self-care",
              examples: [
                "Spa & massage (non-medical)",
                "Hairdresser / barber / grooming",
                "Meditation apps (Calm, Headspace)",
                "Personal stylist sessions",
                "Skincare, cosmetics, natural products",
                "Float therapy, infrared sauna",
              ],
            },
            "travel-experiences": {
              name: "Travel & Experiences",
              description: "Travel credits and local experiences",
              examples: [
                "Airbnb / hotel credits",
                "Airline gift cards",
                "Local tourism activities",
                "Retreats & group experiences (yoga, adventure trips)",
              ],
            },
            "fashion-style": {
              name: "Fashion & Style",
              description: "Clothing, workwear and accessories",
              examples: [
                "Clothing / shoes",
                "Workwear allowances",
                "Glasses (non-prescription/sunglasses)",
                "Blue light glasses",
                "Jewelry, watches, accessories",
              ],
            },
            "fitness-sports": {
              name: "Fitness & Sports",
              description: "Fitness memberships and sports activities",
              examples: [
                "Fitness class passes (ClassPass, spin, boxing)",
                "Sports equipment (rackets, mats, shoes)",
                "Gym memberships",
                "Sports league fees (hockey, soccer, golf, etc.)",
              ],
            },
            "food-nutrition": {
              name: "Food & Nutrition",
              description: "Food-related credits and nutrition",
              examples: [
                "Coffee shop credits",
                "Farmer's market gift cards",
                "Cooking classes",
                "Nutritionist consultations (non-medical)",
              ],
            },
            "pet-care": {
              name: "Pet Care",
              description: "Pet care services and products",
              examples: [
                "Pet insurance premiums",
                "Vet visit reimbursements (non-HSA)",
                "Grooming & training services",
                "Pet daycare & boarding",
              ],
            },
            "relationships-fun": {
              name: "Relationships & Fun",
              description: "Social activities and family fun",
              examples: [
                "Date night expenses",
                "Babysitting reimbursement",
                "Board games & card games",
                "Escape rooms",
                "Karaoke night",
              ],
            },
            "consumer-subscriptions-perks": {
              name: "Consumer Subscriptions & Perks",
              description:
                "Consumer memberships and utility/perk bundles (non-media)",
              examples: [
                "Amazon Prime",
                "Costco/Bulk memberships",
                "Cloud storage or password manager subscriptions",
                "Paid mobile productivity apps (task managers, habit trackers)",
              ],
            },
            "family-kids": {
              name: "Family & Kids",
              description: "Family activities and childcare",
              examples: [
                "Summer camp fees",
                "Extracurriculars (music, dance, coding, art)",
                "Family activity passes (zoos, aquariums, amusement parks)",
                "Daycare",
              ],
            },
          },
        },
        actions: {
          title: "Actions",
          save: "Save Changes",
          saving: "Saving...",
          cancel: "Cancel",
          delete: "Delete Group",
        },
        dialogs: {
          unsaved: {
            title: "Unsaved Changes",
            description:
              "You have unsaved changes. Are you sure you want to leave without saving?",
            keepEditing: "Keep Editing",
            discard: "Discard Changes",
          },
          delete: {
            title: "Delete Group",
            description:
              'This action will permanently delete the "{{name}}" group. All members will lose access to this benefit.',
            confirmPrompt: "To confirm, type the group name:",
            cancel: "Cancel",
            confirm: "Confirm Delete",
          },
          final: {
            title: "Final Confirmation",
            description:
              "Are you absolutely sure you want to delete this group? This action cannot be undone.",
            cancel: "Cancel",
            confirm: "Delete Group",
          },
        },
        toasts: {
          deleteNameMismatch: "Please type the exact group name to confirm deletion",
          updateSuccess: "Group updated successfully!",
          updateFailed: "Failed to update group. Please try again.",
          deleteSuccess: "Group deleted successfully!",
          deleteFailed: "Failed to delete group. Please try again.",
        },
      },
      lifestyleTransactions: {
        header: "LSA Transactions",
        subheader: "Manage and review lifestyle spending transactions",
        breadcrumbs: {
          benefits: "Benefits",
          lifestyleAccount: "Lifestyle Account",
          transactions: "Transactions",
        },
        statusFilterLabel: "Status:",
        statusOptions: {
          all: "All",
          completed: "Completed",
          pending: "Pending",
          incomplete: "Incomplete",
        },
        tableHeaders: {
          brand: "Brand",
          category: "Category",
          amount: "Amount",
          date: "Date",
          status: "Status",
          receipt: "Receipt",
          actions: "Actions",
        },
        receipt: {
          view: "View",
          none: "No receipt",
        },
        statusTags: {
          completed: "Completed",
          pending: "Pending",
          incomplete: "Incomplete",
          denied: "Denied",
          unknown: "Unknown",
        },
        loading: "Loading transactions...",
        loadingMore: "Loading transactions...",
        errorTitle: "Error Loading Transactions",
        errorDescription: "There was a problem loading the transactions",
        retry: "Retry",
        pagination: {
          showing: "Showing",
          to: "to",
          of: "of",
          previous: "Previous",
          next: "Next",
        },
        approveError: {
          title: "Approval failed",
          message:
            "We couldn't approve this transaction. Please try again.",
          ok: "Ok",
        },
        actions: {
          refreshAria: "Refresh transactions",
          approve: "Approve",
          reject: "Reject",
        },
        toasts: {
          missingId: "Missing transaction ID. Please try again.",
          missingPayment: "No payment method available to process this approval.",
          approveSuccess: "Transaction approved",
          approveFailed: "Failed to approve transaction",
          rejectSuccess: "Transaction rejected",
          rejectFailed: "Failed to reject transaction",
        },
        labels: {
          unknownCategory: "Unknown Category",
        },
      },
      profile: {
        subheader: "Complete or update your organization settings here",
        companyInformation: "Company Information",
        companyName: "Company name",
        description: "Description",
        primaryAddress: "Primary company address",
        editCompany: "Edit company",
        editDescription: "Edit description",
        addAddress: "Add address",
        editAddress: "Edit address",
        addressAlert:
          "🚨 Please add your address, then proceed to the {{subscriptionLabel}} screen.",
        saveChanges: "Save changes",
        modal: {
          companyLabel: "Company Modal",
          companyTitle: "Name of company",
          descriptionLabel: "Description Modal",
          descriptionTitle: "Description of company",
          addressLabel: "Address Modal",
          addressSubtitle: "Complete all the fields below",
        },
        fields: {
          street: "Street",
          city: "City",
          country: "Country",
          postalCode: "Postal Code",
          zipCode: "Zip Code",
        },
        errors: {
          companyRequired: "Company name is required",
          descriptionRequired: "Company description is required",
          streetRequired: "Street is required",
          cityRequired: "City is required",
          countryRequired: "Country is required",
          provinceRequired: "Province is required",
          postalCodeRequired: "Postal code is required",
        },
      },
      imageUploader: {
        uploadLogo: "Upload logo",
        updateLogo: "Update logo",
      },
      billing: {
        subheader: "Manage your billing here",
        addressAlert:
          "🚨 First go to the {{organizationLabel}} page and add your address!",
        cardsTitle: "Credit Cards",
        addCard: "Add your card",
        noCards: "No Cards Added yet",
        invoicesTitle: "Invoices",
        noInvoices: "You don't have any invoices yet.",
        modalTitle: "Add a Card",
        registering: "Registering...",
        registerCard: "Register Card",
        cardAdded: "Card added!",
        errors: {
          paymentFailed: "Payment failed",
          paymentFailedWithMessage: "Payment failed: {{message}}",
        },
        paymentMethod: {
          cardFallback: "Card",
          cardEndingIn: "{{brand}} ending in {{lastFourDigits}}",
          expiry: "Expiry {{month}}/{{year}}",
          edit: "Edit",
          loading: "Loading cards...",
          logoAlt: "{{brand}} logo",
        },
        invoices: {
          loading: "Loading...",
          periodStart: "Period Start",
          periodEnd: "Period End",
          amountPaid: "Amount Paid",
          receipt: "Receipt",
          status: "Status",
          paid: "Paid",
          pending: "Pending",
          view: "View",
          noReceipt: "No receipt",
        },
        subscription: {
          yourPlan: "Your Plan",
          planName: {
            basic: "Basic Plan",
            enterprise: "Enterprise Plan",
          },
          planLabel: {
            basic: "Basic",
            enterprise: "Enterprise",
          },
          perMonth: "/ month per user",
          includes: "This includes:",
        },
        subscriptionPoints: {
          onlineRewards: "Online Rewards",
          giftCardRewards: "Gift Card Rewards",
          travelRewards: "Travel Rewards",
          celebrations: "Celebrations",
          airdrops: "Airdrops",
        },
      },
      onboardingProfile: {
        title: "Let's set up your profile",
        subtitle:
          "Tell us a bit about your organisation so we can configure your account.",
      },
      onboardingAddress: {
        title: "Let's add your address",
        subtitle:
          "This will help us get you started on your journey with Parachute.",
        fields: {
          street: "Street",
          city: "City",
          province: "Province",
          country: "Country",
          postalCode: "Postal Code",
        },
      },
      onboardingUploadLogo: {
        title: "Upload your logo",
        subtitleLine1: "Let's personalize your experience.",
        subtitleLine2: "You can also skip this step and get back to it later.",
        next: "Next",
        skip: "Skip this step",
      },
      onboardingAddCard: {
        header: "Add your payment information",
        subheader: "Don't worry - you won't be billed until your team joins.",
        addTeamMembersPrompt: "Now let's add some team members...",
      },
      pendingApproval: {
        header: "Payment Methods",
        subheader: "Manage your billing here",
        loading: "Loading...",
      },
    },
  },
  fr: {
    translation: {
      common: {
        language: "Langue",
        english: "Anglais",
        french: "Français",
        manage: "Gérer",
        viewAll: "Voir tout",
        complete: "Terminé",
        goBack: "Retour",
        continue: "Continuer",
        dashboard: "Tableau de bord",
        learnMore: "En savoir plus",
        year: "année",
        hiWithName:
          "Bonjour {{name}}, voici tout ce dont vous avez besoin pour gérer votre compte Parachute Partners.",
        hiFallbackName: "vous",
        monthNamesShort: [
          "janv.",
          "févr.",
          "mars",
          "avr.",
          "mai",
          "juin",
          "juil.",
          "août",
          "sept.",
          "oct.",
          "nov.",
          "déc.",
        ],
      },
      nav: {
        home: "Accueil",
        organization: "Organisation",
        subscription: "Abonnement",
        benefits: "Avantages",
        team: "Équipe",
        signOut: "Se déconnecter",
      },
      setup: {
        finishSetup: "Terminez la configuration de votre compte:",
      },
      todos: {
        addBillingTitle: "Ajoutez vos informations de facturation",
        inviteMemberTitle: "Invitez votre premier membre d'équipe !",
        descriptionPlaceholder: "",
      },
      hero: {
        teamMembers: "Membres de l'équipe",
        onboardedMembers: "Membres intégrés",
        onboardedRate: "Taux d'employés intégrés",
        totalRewards: "Récompenses totales",
      },
      lifestyle: {
        title: "Compte style de vie",
        manage: "Gérer",
        noTeamMembersTitle: "Aucun membre",
        noTeamMembersSubtitle: "Invitez votre équipe à collaborer sur ce projet.",
        inviteMembers: "Inviter des membres",
        noGroupTitle: "Aucun groupe style de vie",
        noGroupSubtitle: "Créez un groupe pour vos membres.",
        createGroup: "Créer un groupe style de vie",
        noBenefitsTitle: "Aucun avantage style de vie n'est configuré",
        noBenefitsSubtitle: "Aidez votre équipe avec leurs dépenses bien-être",
        createBenefits: "Créer mon compte d'avantages style de vie",
        totalRequested: "Total demandé",
        employeeParticipation: "Participation des employés",
        avgPerUser: "Moyenne par utilisateur",
        recentPending: "Transactions en attente récentes",
        noPending: "Aucune transaction en attente pour le moment.",
        pendingUser: "Utilisateur en attente",
      },
      benefits: {
        title: "Avantages",
      },
      team: {
        title: "Équipe",
        teamMembers: "Membres de l'équipe",
        onboardedMembers: "Membres intégrés",
        onboardedRate: "Taux d'employés intégrés",
        onboardingProgress: "Progression de l'intégration",
        allOnboarded: "Tous les utilisateurs sont intégrés",
        noMembersTitle: "Aucun membre pour le moment",
        noMembersSubtitle: "Invitez vos employés à commencer avec Parachute.",
        inviteMembers: "Inviter des membres",
        onboardedSummary: "{{onboarded}} sur {{total}} intégrés",
      },
      rewards: {
        title: "Performance des récompenses",
        monthlyTrend: "Tendance des économies mensuelles",
        xAxis: "Mois",
        yAxis: "Économies ($)",
        annualSavings: "Économies annuelles de l'équipe",
        monthlySavings: "Économies mensuelles de l'équipe",
        boostOn: "Boost - Activé",
        boostOff: "Boost - Désactivé",
        tooltipLabel: "Économies",
        noData: "Aucune donnée de performance des récompenses pour le moment",
        noDataSubtitle:
          "Les informations apparaîtront lorsqu'il y aura des récompenses à analyser",
      },
      airdrops: {
        title: "Airdrops récents",
        viewAll: "Voir tout",
        none: "Aucun airdrop envoyé pour le moment",
        noneCta: "Envoyez votre premier airdrop",
        noneSubtitle: "pour récompenser vos membres d'équipe",
      },
      airdrop: {
        common: {
          header: "Airdrops",
          subheader: "Envoyez des cartes cadeaux à vos employés",
          breadcrumbs: {
            benefits: "Avantages",
            airdrops: "Airdrops",
            selectMembers: "Choisir les membres",
            selectGiftCard: "Choisir la carte cadeau",
            selectAmount: "Choisir le montant",
            confirm: "Confirmer",
          },
          other: "Autre",
        },
        hero: {
          kicker: "Airdrop de cartes cadeaux",
          title: "Reconnaissez les efforts instantanément.",
          description:
            "Envoyez des cartes cadeaux directement à vos employés, dans l'application Parachute. Choisissez parmi plus de 100 marques. Les airdrops rendent la reconnaissance simple et percutante.",
          cta: "Envoyer une carte cadeau",
          alt: "Illustration de récompenses par carte cadeau",
        },
        summaryCards: {
          totalAirdropsSent: "Total des airdrops envoyés",
          totalValueSent: "Valeur totale envoyée",
        },
        history: {
          title: "Historique de vos airdrops",
          searchPlaceholder: "Rechercher par nom ou marque",
          loading: "Chargement de l'historique des airdrops...",
          errorTitle: "Erreur de chargement de l'historique des airdrops",
          errorSubtitle: "Veuillez réessayer plus tard",
          tableHeaders: {
            fullName: "Nom complet",
            date: "Date",
            brand: "Marque",
            totalSaleAmount: "Montant total de la vente",
            receipt: "Reçu",
          },
          receipt: {
            view: "Voir",
            none: "Aucun reçu",
          },
          emptySearch: "Aucun airdrop ne correspond à votre recherche.",
          emptyHistory:
            "Vous n'avez pas encore envoyé d'airdrops à vos employés.",
        },
        selectMembers: {
          title: "À qui l'envoyez-vous ?",
          searchPlaceholder: "Rechercher par nom ou courriel",
          tableHeaders: {
            select: "Sélectionner",
            name: "Nom",
            email: "Courriel",
            workAnniversary: "Anniversaire d'emploi",
            birthday: "Anniversaire",
            status: "Statut",
          },
          status: {
            inProgress: "En cours",
            onboarded: "Intégré",
          },
          ariaSelect: "Sélectionner {{email}}",
          emptySearch: "Aucun membre de l'équipe ne correspond à votre recherche.",
          emptyState: "Vous n'avez actuellement aucun membre intégré.",
        },
        selectOffers: {
          title: "Quelle récompense voulez-vous envoyer ?",
          searchPlaceholder: "Rechercher par nom d'offre",
          loading: "Les offres de cartes cadeaux sont en cours de chargement...",
        },
        selectAmount: {
          title: "Quel montant ?",
          presetLabel: "Montants prédéfinis",
          customLabel: "Ou saisir un montant personnalisé",
          customPlaceholder: "Saisir un montant",
          messageLabel: "Ajouter un court message",
          messagePlaceholder: "100 caractères maximum",
          requireOffer:
            "Veuillez choisir une carte cadeau avant de sélectionner le montant.",
        },
        confirm: {
          title: "Confirmez votre airdrop",
          summary:
            "Vous envoyez une carte cadeau {{brand}} de {{amount}} à {{name}}",
          messageIncluded: '"{{message}}"',
          messageMissing: "(aucun message inclus)",
          paymentLine:
            "Courriel : {{email}} | Paiement : {{cardBrand}} se terminant par {{last4}}",
          notification:
            "{{name}} recevra une notification push et un courriel (si activés). Des instructions seront fournies pour utiliser la carte cadeau.",
          modal: {
            title: "Confirmer l'airdrop",
            description:
              "Veuillez confirmer votre carte cadeau {{brand}} de {{amount}} pour {{name}}.",
            confirm: "Envoyer !",
          },
          sendAirdrop: "Envoyer l'airdrop",
        },
        todoCard: {
          title: "Envoyer un airdrop à un coéquipier !",
        },
        complete: {
          breadcrumbs: {
            complete: "Terminé",
          },
          successTitle: "Félicitations, votre carte-cadeau vient d'être envoyée !",
          sendAnother: "Envoyer un autre airdrop",
          returnHome: "Retourner à l'accueil",
        },
      },
      auth: {
        common: {
          backToHome: "Retour à l'accueil",
          backToSignIn: "Retour à la connexion",
          signIn: "Se connecter",
          signInCta: "Connexion",
          signUp: "Créez votre compte",
          or: "Ou",
          forgotPassword: "Mot de passe oublié ?",
          continueWithGoogle: "Continuer avec Google",
          devBanner: "VERSION DEV",
          betaBanner: "VERSION BÊTA",
        },
        signup: {
          title: "Créez votre compte",
          subtitlePrompt: "Vous avez déjà un compte ?",
          signInLink: "Se connecter",
          firstName: "Prénom",
          lastName: "Nom",
          email: "Adresse courriel",
          password: "Mot de passe",
          confirmPassword: "Confirmer le mot de passe",
          submit: "Créer un compte",
          continueWithGoogle: "Continuer avec Google",
          devBanner: "VERSION DEV",
          betaBanner: "VERSION BÊTA",
        },
        signin: {
          title: "Connectez-vous à votre compte",
          subtitlePrompt: "Vous n'avez pas de compte ?",
          signUpLink: "Créer un compte",
          email: "Adresse courriel",
          password: "Mot de passe",
          rememberMe: "Se souvenir de moi",
          submit: "Connexion",
        },
        forgotPassword: {
          title: "Mot de passe oublié ?",
          subtitle:
            "Entrez votre courriel et nous vous enverrons les instructions pour réinitialiser votre mot de passe.",
          emailPlaceholder: "Adresse courriel",
          submit: "Envoyer le lien de réinitialisation",
          backToSignIn: "Retour à la connexion",
        },
        resetPassword: {
          title: "Réinitialiser le mot de passe",
          subtitle: "Saisissez votre nouveau mot de passe ci-dessous.",
          newPassword: "Nouveau mot de passe",
          confirmPassword: "Confirmer le nouveau mot de passe",
          submit: "Mettre à jour le mot de passe",
          successTitle: "Mot de passe réinitialisé",
          successDescription:
            "Votre mot de passe a été réinitialisé. Vous pouvez maintenant vous connecter au tableau de bord.",
          backToSignIn: "Retour à la connexion",
        },
        forceResetPassword: {
          title: "Choisissez un mot de passe",
          subtitle: "Veuillez choisir un nouveau mot de passe pour continuer.",
          newPassword: "Nouveau mot de passe",
          confirmPassword: "Confirmer le nouveau mot de passe",
          submit: "Mettre à jour le mot de passe",
          successTitle: "Succès !",
          successDescription:
            "Votre mot de passe a été changé. Vous pouvez maintenant vous connecter au tableau de bord.",
          backToSignIn: "Retour à la connexion",
        },
        pending: {
          title: "Compte en attente d'approbation",
          description:
            "Votre compte est en cours de revue. Nous vous enverrons un courriel lorsque vous serez approuvé.",
          backToHome: "Retour à l'accueil",
        },
        confirmEmail: {
          title: "Vérifiez votre courriel",
          instructions:
            "Nous vous avons envoyé un courriel avec un lien pour confirmer votre compte Parachute Partners.",
          noEmail: "Vous n'avez pas reçu de courriel ?",
          resend: "Renvoyer la confirmation",
        },
        requestSent: {
          title: "Demande en attente",
          description:
            "Votre demande pour rejoindre Parachute Partners a été envoyée à notre équipe Partenariats. Nous vous informerons par courriel une fois qu'elle sera approuvée.",
          questions: "Des questions ?",
          sendEmail: "Envoyez-nous un courriel !",
        },
        accountConfirmed: {
          successTitle: "Vérification du compte réussie !",
          successDescription:
            "Votre compte a été vérifié. Vous pouvez maintenant vous connecter et commencer votre expérience Parachute.",
          expiredTitle: "Vérification du compte expirée",
          expiredDescription:
            "Le courriel de vérification que vous avez utilisé a expiré. Cela peut être parce que vous avez déjà vérifié votre compte ou parce que le lien a expiré. Veuillez demander un nouveau courriel de vérification.",
          invalidTitle: "Lien de vérification invalide",
          invalidDescription:
            "Le lien de vérification que vous avez utilisé est invalide. Veuillez utiliser le dernier lien reçu ou demander un nouveau courriel de vérification.",
          alreadyTitle: "Vérification déjà effectuée",
          alreadyDescription:
            "Votre compte a déjà été vérifié. Veuillez vous connecter pour continuer. Si vous pensez qu'il s'agit d'une erreur, contactez le support.",
          requestNew: "Demander un nouveau courriel de vérification",
        },
        userConfirmed: {
          title: "Votre compte utilisateur a été confirmé !",
          description:
            "Vous pouvez maintenant retourner dans l'application et vous connecter pour commencer votre expérience Parachute.",
        },
        welcome: {
          title: "Configurons votre profil",
          subtitle:
            "Parlez-nous un peu de votre organisation afin que nous configurions votre compte.",
          organization: "Nom de l'organisation",
          website: "Votre site web",
          phoneNumber: "Numéro de téléphone",
          description: "Décrivez votre organisation",
          submit: "Créer mon compte",
        },
      },
      cashbackRewards: {
        header: "Récompenses cashback",
        subheader: "Gérez votre programme de récompenses ici",
        breadcrumbs: {
          benefits: "Avantages",
          cashback: "Récompenses cashback",
        },
        hero: {
          kicker: "Programme de récompenses cashback",
          title: "Boostez chaque achat avec du cashback financé par l'employeur.",
          description:
            "Fixez un boost financé par l'entreprise pour que votre équipe gagne plus de récompenses lors des achats dans l'application Parachute. Choisissez le pourcentage, plafonnez-le par employé et ajustez-le à tout moment selon votre budget.",
          alt: "Illustration des récompenses cashback",
        },
        boostSection: {
          title: "Booster les récompenses",
          boostOffersBy: "Booster les offres de",
          amountPlaceholder: "Montant",
          boostLimitLabel: "Définir une limite de boost  $",
          boostLimitPlaceholder: "Montant",
          helperBoost:
            "Activer cette fonctionnalité permettra à votre équipe de gagner des récompenses plus élevées chez les marques partenaires (facturation mensuelle à votre organisation).",
          helperLimit: "Définissez un plafond annuel de boost par employé.",
          saving: "Enregistrement...",
          confirm: "Confirmer les changements",
        },
      },
      groupInsurance: {
        header: "Assurance collective",
        subheader: "Gérez vos options d'assurance collective ici",
        hero: {
          kicker: "Bientôt disponible !",
          title: "Protégez votre équipe avec une couverture flexible.",
          description:
            "Offrez à vos employés la sécurité qu'ils méritent. Configurez et gérez facilement l'assurance santé, dentaire et invalidité, et ajustez les plans à mesure que votre organisation grandit.",
          notAvailable: "Cette fonctionnalité n'est pas encore disponible. Restez à l'écoute !",
          alt: "Illustration d'assurance collective",
        },
      },
      lifestyleCategories: {
        header: "Compte style de vie",
        subheader: "Automatisez les remboursements sur certaines dépenses",
        breadcrumbs: {
          benefits: "Avantages",
          lifestyle: "Compte style de vie",
          createGroup: "Créer un groupe",
        },
        step1Title: "Nommez votre groupe style de vie",
        step1Description:
          "Donnez à votre groupe d'avantages style de vie un nom que vos employés reconnaîtront.",
        groupNameLabel: "Nom du groupe style de vie",
        groupNamePlaceholder: "ex. Avantages bien-être employés",
        groupNameHelper: "Ce nom sera visible par vos employés",
        step2Title: "Sélectionnez les catégories",
        step2Description:
          "Choisissez les catégories de dépenses style de vie à inclure dans votre groupe d'avantages (vous pourrez les modifier plus tard).",
        selectAll: "Tout sélectionner",
        loading: "Chargement des catégories...",
        examplesTitle: "Exemples",
        noExamples: "Aucun exemple disponible pour cette catégorie.",
      },
      lifestyleRules: {
        header: "Compte style de vie",
        subheader: "Automatisez les remboursements sur certaines dépenses",
        breadcrumbs: {
          benefits: "Avantages",
          lifestyle: "Compte style de vie",
          rules: "Règles",
        },
        stepTitle: "Créez vos règles",
        stepDescription:
          "Configurez les règles et limites pour vos avantages style de vie.",
        generalRules: "Règles générales",
        yearlyAmountLabel: "Montant annuel par employé",
        yearlyAmountHelper: "Montant annuel disponible par employé",
        coverageLabel: "Couverture générale",
        coverageHelper: "Pourcentage des factures remboursées",
        allocationTitle: "Choix d'allocation",
        allocationSubtitle:
          "Choisissez comment le montant annuel est distribué aux employés",
        allocationOptions: {
          proRataTitle: "Système au prorata",
          proRataDescription:
            "Les employés reçoivent un montant proportionnel selon la date d'activation du programme. Par exemple, si activé à mi-année, ils obtiennent 50 % du montant annuel.",
          fullAmountTitle: "Montant complet",
          fullAmountDescription:
            "Les employés reçoivent le montant annuel complet, peu importe la date d'activation du programme.",
        },
      },
      lifestyleConfirm: {
        header: "Compte style de vie",
        subheader: "Automatisez les remboursements sur certaines dépenses",
        breadcrumbs: {
          benefits: "Avantages",
          lifestyle: "Compte style de vie",
          confirm: "Confirmer",
        },
        title: "Vérifiez et confirmez",
        description:
          "Vérifiez vos paramètres d'avantages style de vie avant de finaliser.",
        groupName: "Nom du groupe",
        categories: "Catégories sélectionnées",
        rules: "Règles",
        yearlyAmount: "Montant annuel par employé",
        coverage: "Couverture générale",
        allocation: "Choix d'allocation",
      },
      brands: {
        title: "Marques populaires",
        purchase: "achat",
        purchases: "achats",
        noData: "Aucune donnée sur les marques populaires pour le moment",
        noDataSubtitle:
          "Les marques apparaîtront ici lorsque les utilisateurs commenceront à faire des achats",
      },
      perks: {
        header: "Compte d'avantages",
        subheader: "Créez et gérez votre compte d'avantages ici",
        hero: {
          kicker: "Soutenez votre équipe de façon utile",
          title: "Améliorez votre expérience des avantages.",
          description:
            "Offrez à votre organisation des avantages flexibles et utiles. Des récompenses en argent aux allocations bien-être, tout est géré facilement avec Parachute.",
        },
        sections: {
          active: "Actifs",
          addMore: "Ajoutez ces avantages pour votre équipe",
        },
        status: {
          active: "Actif",
          beta: "Bêta",
          comingSoon: "Bientôt disponible",
        },
        itemDetails: {
          waitlistCta: "Accédez en avant-première en rejoignant la liste d'attente!",
          featureActive: "Fonction active",
          joinWaitlist: "Rejoindre la liste d'attente",
          alreadyIncluded: "Déjà inclus",
          activate: "Activer {{name}}",
          deactivate: "Désactiver {{name}}",
          close: "Fermer",
        },
        items: {
          perk_airdrop: {
            title: "Airdrops",
            fullTitle: "Airdrops",
            description: "Envoyez des cartes-cadeaux numériques à votre équipe et plus encore",
          },
          perk_allCashback: {
            title: "Récompenses",
            fullTitle: "Récompenses de remise en argent",
            description: "Gagnez chez plus de 1000 marques",
          },
          perk_onTheGo: {
            title: "Style de vie",
            fullTitle: "Style de vie",
            description: "Automatisez le remboursement des dépenses sélectionnées",
          },
          perk_celebrations: {
            title: "Célébrations",
            fullTitle: "Célébrations",
            description: "Automatisez les célébrations (anniversaires, fêtes, etc.)",
          },
          perk_slack: {
            title: "Reconnaissance",
            fullTitle: "Reconnaissance",
            description: "Laissez votre équipe se récompenser pour les grands succès !",
          },
          perk_24_7: {
            title: "Paie 24/7",
            fullTitle: "Paie 24/7",
            description: "Accédez à votre paie quand vous en avez le plus besoin",
          },
          perk_customerRewards: {
            title: "Récompenses clients",
            fullTitle: "Récompenses clients",
            description: "Envoyez des cartes-cadeaux à vos clients",
          },
        },
      },
      perksSection: {
        groupInsurance: {
          title: "Assurance collective",
          subtitle: "Protégez votre équipe. Laissez Parachute trouver la meilleure assurance.",
          statusProtected: "Protégé",
          statusNotProtected: "Non protégé",
          expiresOn: "Expire le {{date}}",
          noExpiry: "N/D",
          alt: "Icône de couverture protégée",
        },
        myBenefits: "Mes avantages Parachute",
      },
      teamPage: {
        header: "Équipe",
        subheader: "Gérez vos invitations Parachute ici",
        addressAlert: "🚨 Rendez-vous d'abord sur la page Organisation et ajoutez votre adresse !",
        addCardAlert:
          "🚨 Cliquez ici pour aller à la page Abonnement et ajouter une carte de crédit !",
        membersCount: "{{count}} membres",
        searchPlaceholder: "Rechercher par nom ou courriel",
        addMember: "+ Ajouter un membre",
        tableHeaders: {
          name: "Nom",
          email: "Courriel",
          workAnniversary: "Anniversaire d'emploi",
          birthday: "Anniversaire",
          status: "Statut",
          actions: "Actions",
        },
        status: {
          inProgress: "En cours",
          onboarded: "Intégré",
          readyToImport: "Prêt à importer",
          needsReview: "À vérifier",
        },
        emptySearch: "Aucun membre ne correspond à votre recherche.",
        emptyTitle: "Il est temps d'inviter votre premier membre.",
        emptyDescription:
          "Commencez à bâtir votre équipe en ajoutant votre premier employé. Il recevra une invitation pour rejoindre votre programme Parachute.",
        pagination: {
          showing: "Affichage de",
          to: "à",
          of: "sur",
          previous: "Précédent",
          next: "Suivant",
        },
        modals: {
          addTitle: "Ajouter un membre",
          editTitle: "Modifier un membre",
          deleteTitle: "Supprimer l'utilisateur",
          deleteMessage: "Retirer {{name}} de l'équipe ?",
          resendTitle: "Renvoyer l'invitation",
          resendMessage: "Renvoyer un courriel d'invitation Parachute à {{name}} ?",
          resendSuccessTitle: "Invitation envoyée",
          resendSuccessMessage: "Courriel d'invitation envoyé à {{name}}.",
          importPreviewTitle: "Aperçu de l'importation de l'équipe",
          importLoadingTitle: "Importation des membres...",
          importLoadingStatus: "{{completed}} sur {{total}}",
          importLoadingPreparing: "Préparation de l'importation...",
          importPreviewLoading: "Chargement de l'aperçu...",
          importPreviewEmpty: "Aucune ligne trouvée dans ce fichier.",
          importPreviewTotals: {
            usersReady: "Utilisateurs prêts à importer",
            invalidEntries: "Entrées invalides",
            importingOnlyValid: "Seuls les utilisateurs valides seront importés.",
          },
          importActions: {
            cancel: "Annuler",
            import: "Importer",
            importing: "Importation...",
          },
          labels: {
            firstName: "Prénom",
            lastName: "Nom",
            email: "Adresse courriel",
            emailLanguage: "Langue du courriel",
            languageEnglish: "Anglais",
            languageFrench: "Français",
            workAnniversary: "Anniversaire d'emploi (optionnel)",
            birthday: "Anniversaire (optionnel)",
            selectDate: "Sélectionner une date",
            autoSignUp: "Inscription automatique",
            autoSignUpHelper: "Créer automatiquement le compte mobile et envoyer le mot de passe par courriel. Aucune activation requise.",
            sendInvite: "Envoyer l'invitation",
            updateMember: "Mettre à jour le membre",
            confirm: "Confirmer",
            ok: "Ok",
          },
        },
        import: {
          importTitle: "Importer",
          menuLabel: "Options d'importation",
          importFile: "Importer un Excel ou CSV…",
          downloadTemplate: "Télécharger le modèle CSV",
          downloadTemplateXlsx: "Télécharger le modèle Excel",
        },
        actions: {
          edit: "Modifier",
          remove: "Supprimer",
          clearSearch: "Effacer la recherche",
        },
        importPreview: {
          statusReady: "Prêt à importer",
          statusNeedsReview: "À vérifier",
        },
        errors: {
          genericImportTitle: "Problème d'import",
          genericImportSubtitle:
            "Le fichier d'import semble mal formaté. Veuillez télécharger un modèle via le menu Importer (format Excel ou CSV).",
          emailRequired: "Le courriel est requis",
          emailInvalid: "Adresse courriel invalide",
          firstNameRequired: "Le prénom est requis",
          lastNameRequired: "Le nom est requis",
          emailAlreadyAdded: "Cette adresse courriel a déjà été ajoutée comme membre !",
          csvEmpty: "Le fichier sélectionné est vide.",
          csvNoData: "Aucune ligne de données n'a été trouvée dans le fichier.",
          missingColumns: "Colonne(s) requise(s) manquante(s): {{columns}}",
          missingPayment: "Ajoutez un mode de paiement avant d'importer des membres.",
          noRowsToImport: "Aucune ligne à importer.",
          noValidRows: "Aucune ligne valide à importer. Vérifiez les problèmes indiqués.",
          csvReadError:
            "Impossible de lire ce fichier. Vérifiez le format et réessayez.",
          noValidSelected: "Aucune ligne valide sélectionnée pour l'importation.",
          userAlreadyOnTeam: "L'utilisateur est déjà dans votre équipe.",
          duplicateInFile: "{{email}} apparaît plusieurs fois dans ce fichier.",
          missingNameForEmail: "Prénom ou nom manquant pour {{email}}.",
          missingEmail: "Adresse courriel manquante.",
          unsupportedFile: "Type de fichier non pris en charge. Téléversez un fichier CSV ou Excel.",
        },
        toasts: {
          importCancelled:
            "Importation annulée après {{count}} sur {{total}} membre(s) importé(s).",
          importSuccess: "{{count}} membre(s) importé(s).",
          importSkippedSummary:
            "{{count}} ligne(s) ignorée(s). {{summary}}{{remaining}}",
          rowsSkippedRemaining: " ...et {{remaining}} de plus.",
          rowAlreadyOnTeam:
            "Ligne {{line}}: {{email}} est déjà dans votre équipe.",
          rowImportFailed:
            "Ligne {{line}}: impossible d'importer {{email}}.",
        },
        importResults: {
          successBoxTitle: "Importations réussies",
          failureBoxTitle: "Importations échouées",
          exportFailures: "Télécharger le rapport d'erreurs",
          exportDescription:
            "Génère un nouveau fichier {{ext}} contenant les utilisateurs qui n'ont pas pu être importés.",
        },
      },
      lifestyleMain: {
        header: "Compte style de vie",
        subheader: "Automatisez les remboursements sur certaines dépenses",
        breadcrumbs: {
          benefits: "Avantages",
          lifestyleAccount: "Compte style de vie",
        },
        setup: {
          title: "Dynamisez votre équipe",
          subtitle: "Des avantages lifestyle efficaces",
          description:
            "Créez un compte style de vie personnalisé qui rembourse automatiquement vos employés pour leurs dépenses quotidiennes. Améliorez le moral, attirez les meilleurs talents et montrez que vous tenez à leur équilibre travail-vie personnelle.",
          cta: "Créer un compte style de vie",
          cards: {
            automaticReimbursements: {
              title: "Remboursements automatiques",
              description:
                "Configurez des règles et laissez le système gérer les remboursements automatiquement. Fini les notes de frais manuelles.",
            },
            customCategories: {
              title: "Catégories personnalisées",
              description:
                "Choisissez parmi plus de 15 catégories lifestyle ou créez les vôtres. Adaptez les avantages aux besoins de votre équipe.",
            },
            insights: {
              title: "Analyses et informations",
              description:
                "Suivez les habitudes de dépenses, les catégories populaires et mesurez l'impact de vos avantages lifestyle.",
            },
          },
        },
        loading: {
          title: "Chargement des analyses",
          description:
            "Récupération des données analytiques de vos avantages style de vie...",
        },
        error: {
          title: "Erreur de chargement des données",
          description:
            "Un problème est survenu lors du chargement de vos données analytiques. Veuillez réessayer.",
          retry: "Recharger les données",
        },
        filters: {
          groupLabel: "Groupe:",
          periodLabel: "Période:",
          manage: "Gérer",
          groupOptionLabel: "{{name}} ({{count}} membre)",
          groupOptionLabel_plural: "{{name}} ({{count}} membres)",
          groupOptionComingSoon:
            "Créer un autre groupe style de vie (bientôt disponible)",
          timeRanges: {
            last7Days: "7 derniers jours",
            last30Days: "30 derniers jours",
            yearToDate: "Depuis le début de l'année",
            last12Months: "12 derniers mois",
          },
        },
        summary: {
          title: "Résumé",
          yearlyBudget: "Budget annuel par utilisateur (maximum)",
          coverageRate: "Taux de couverture",
          coveredCategories: "Catégories couvertes",
          categoriesCount: "({{count}})",
          empty: "Aucune demande pour ce groupe pour le moment.",
        },
        covered: {
          title: "Personnes couvertes",
          membersCount: "({{count}} membre)",
          membersCount_plural: "({{count}} membres)",
          fallbackName: "Membre",
          tableHeaders: {
            name: "Nom",
            email: "Courriel",
          },
          noSearchResults:
            "Aucun membre de l'équipe ne correspond à votre recherche.",
          noMembersTitle: "Aucun membre de l'équipe",
          noMembersSubtitle:
            "Invitez votre équipe à collaborer sur ce projet.",
          invite: "Inviter des membres",
        },
        performance: {
          title: "Performance",
          totalRequested: "Total demandé",
          averagePerMember: "Moyenne par membre",
          participationRate: "Taux de participation",
        },
        pending: {
          title: "Transactions en attente",
          emptyTitle: "Aucune transaction en attente",
          emptySubtitle:
            "Les transactions apparaîtront ici lorsque les employés soumettront leurs dépenses",
        },
        history: {
          title: "Historique des transactions",
          emptyTitle: "Aucun historique pour la période sélectionnée",
          emptySubtitle:
            "Les transactions complétées ou refusées apparaîtront ici",
          statusFallback: "Terminé",
        },
        categories: {
          popularTitle: "Catégories les plus populaires",
          popularDescription:
            "Dépense totale par catégorie pour {{timeRange}}",
          timeRanges: {
            last7Days: "les 7 derniers jours",
            last30Days: "les 30 derniers jours",
            yearToDate: "l'année en cours",
            last12Months: "les 12 derniers mois",
          },
          avgReimbursement: "Remboursement moyen par demande",
          spendingByCategoryTitle: "Dépenses par catégorie",
          topLabel: "(Top 5)",
          other: "Autre",
        },
      },
      lifestyleManageGroup: {
        header: "Gérer le groupe",
        subheader:
          "Modifiez les paramètres de votre compte style de vie et gérez les avantages des membres",
        breadcrumbs: {
          benefits: "Avantages",
          lifestyleAccount: "Compte style de vie",
          manageGroup: "Gérer le groupe",
        },
        loading: {
          subheader: "Chargement des détails du groupe...",
        },
        error: {
          subheader: "Erreur de chargement des détails du groupe",
          title: "Erreur de chargement du groupe",
          description: "Erreur de chargement des détails du groupe",
        },
        notFound: {
          subheader: "Groupe introuvable",
          title: "Groupe introuvable",
          description: "Le groupe demandé est introuvable.",
        },
        form: {
          title: "Informations de base",
          fields: {
            groupName: "Nom du groupe",
            yearlySpendingLimit: "Plafond annuel de dépenses",
            coveragePercentage: "Pourcentage de couverture",
            resetDate: "Date de réinitialisation",
            runOffPeriod: "Période de grâce (jours)",
            prorated: "Activer les avantages au prorata pour les nouveaux membres",
          },
          placeholders: {
            groupName: "Saisir le nom du groupe",
            yearlySpendingLimit: "2500",
            coveragePercentage: "80",
            runOffPeriod: "30",
            deleteConfirmation: "Saisir le nom du groupe ici",
          },
          validation: {
            groupNameRequired: "Le nom du groupe est requis",
            groupNameMinLength: "Le nom du groupe doit comporter au moins 3 caractères",
            yearlySpendingLimit: "Le plafond annuel de dépenses doit être supérieur à 0",
            coveragePercentage: "Le pourcentage de couverture doit être entre 0 et 100",
            runOffPeriod: "La période de grâce doit être comprise entre 0 et 365 jours",
            noGroupSelected: "Aucun groupe sélectionné",
            fixValidation: "Corrigez les erreurs de validation avant d'enregistrer",
          },
        },
        categories: {
          title: "Catégories admissibles",
          description:
            "Choisissez les catégories admissibles aux demandes ({{count}} sélectionnée)",
          description_plural:
            "Choisissez les catégories admissibles aux demandes ({{count}} sélectionnées)",
          infoTitle: "Exemples",
          noExamples: "Aucun exemple disponible pour cette catégorie.",
          categoryDetails: {
            "home-daily-life": {
              name: "Vie quotidienne et domicile",
              description:
                "Services et abonnements pour la maison et le quotidien (sans matériel de bureau)",
              examples: [
                "Services de ménage",
                "Collecte de linge / pressing",
                "Abonnements de livraison d'épicerie (ex. Instacart+)",
                "Abonnements de kits repas (ex. Goodfood, HelloFresh)",
                "Appareils maison connectés (ex. éclairage, enceintes)",
              ],
            },
            "remote-work-productivity": {
              name: "Télétravail et productivité",
              description:
                "Dépenses de télétravail, mobilier de bureau et outils de productivité",
              examples: [
                "Frais d'espace de coworking",
                "Améliorations Internet / remboursements partiels",
                "Accessoires tech (souris, webcam, clavier)",
                "Bureaux assis-debout, outils ergonomiques",
                "Mobilier de bureau à domicile (chaise, bureau, support d'écran)",
                "Lunettes anti-lumière bleue",
              ],
            },
            "transportation-eco-mobility": {
              name: "Transport et écomobilité",
              description: "Transport écologique et pratique",
              examples: [
                "Passes de transport en commun",
                "Abonnements vélo en libre-service",
                "Subvention d'achat de vélo ou trottinette électrique",
                "Abonnements d'autopartage (ex. Communauto, Zipcar)",
                "Lavage et entretien automobile",
                "Changement / entreposage de pneus d'hiver",
              ],
            },
            "culture-live-events": {
              name: "Culture et spectacles en direct",
              description:
                "Expériences culturelles (musées, galeries, théâtre, concerts)",
              examples: [
                "Passes de musée ou galerie",
                "Billets de théâtre ou de concert",
                "Billets de festival (musique, arts, culture)",
                "Spectacles d'humour",
              ],
            },
            "entertainment-subscriptions": {
              name: "Divertissement et médias",
              description: "Streaming, médias et jeux",
              examples: [
                "Netflix, Disney+, Spotify",
                "Billets de cinéma",
                "Abonnements de jeux (Xbox, Playstation, Nintendo)",
                "Audiolivres et balados (Audible, Wondery+)",
              ],
            },
            "learning-personal-growth": {
              name: "Apprentissage et développement personnel",
              description: "Cours, livres et développement professionnel",
              examples: [
                "Cours (LinkedIn Learning, Coursera, Udemy)",
                "Abonnements de cours créatifs (Masterclass, Skillshare)",
                "Applications d'apprentissage linguistique (Duolingo Plus, Babbel)",
                "Livres et livres numériques",
                "Billets de conférence",
                "Coaching de carrière",
                "Ateliers de littératie financière",
                "Cours de prise de parole",
              ],
            },
            "hobbies-creative": {
              name: "Loisirs et fournitures créatives",
              description: "Matériel pour hobbies créatifs et bricolage",
              examples: [
                "Fournitures artistiques (peintures, pinceaux, toiles)",
                "Matériel d'artisanat (laine, tissu, perles)",
                "Outils de bricolage non professionnels",
                "Maquettes et kits (maquettes, LEGO)",
                "Accessoires photo (trépieds, filtres, éclairage)",
                "Accessoires d'instruments de musique",
              ],
            },
            "wellness-self-care": {
              name: "Bien-être et soins personnels",
              description: "Bien-être personnel et soins de soi",
              examples: [
                "Spa et massage (non médical)",
                "Coiffure / barbier / soins",
                "Applications de méditation (Calm, Headspace)",
                "Séances de styliste personnel",
                "Soins de la peau, cosmétiques, produits naturels",
                "Flottaison, sauna infrarouge",
              ],
            },
            "travel-experiences": {
              name: "Voyages et expériences",
              description: "Crédits voyage et activités locales",
              examples: [
                "Crédits Airbnb / hôtels",
                "Cartes-cadeaux de compagnies aériennes",
                "Activités touristiques locales",
                "Retraites et expériences de groupe (yoga, voyages d'aventure)",
              ],
            },
            "fashion-style": {
              name: "Mode et style",
              description: "Vêtements, tenue de travail et accessoires",
              examples: [
                "Vêtements / chaussures",
                "Allocations pour tenue de travail",
                "Lunettes (sans prescription / soleil)",
                "Lunettes anti-lumière bleue",
                "Bijoux, montres, accessoires",
              ],
            },
            "fitness-sports": {
              name: "Fitness et sports",
              description: "Abonnements et activités sportives",
              examples: [
                "Passes de cours de fitness (ClassPass, spin, boxe)",
                "Équipement sportif (raquettes, tapis, chaussures)",
                "Abonnements de gym",
                "Frais de ligue sportive (hockey, soccer, golf, etc.)",
              ],
            },
            "food-nutrition": {
              name: "Alimentation et nutrition",
              description: "Crédits liés à l'alimentation et à la nutrition",
              examples: [
                "Crédits de café",
                "Cartes-cadeaux de marché fermier",
                "Cours de cuisine",
                "Consultations nutritionnelles (non médicales)",
              ],
            },
            "pet-care": {
              name: "Soins pour animaux",
              description: "Services et produits pour animaux",
              examples: [
                "Primes d'assurance pour animaux",
                "Remboursement de visites chez le vétérinaire (hors HSA)",
                "Services de toilettage et dressage",
                "Garderie et pension pour animaux",
              ],
            },
            "relationships-fun": {
              name: "Relations et loisirs",
              description: "Activités sociales et loisirs en famille",
              examples: [
                "Dépenses de sortie en couple",
                "Remboursement de garde d'enfants",
                "Jeux de société et de cartes",
                "Jeux d'évasion",
                "Soirée karaoké",
              ],
            },
            "consumer-subscriptions-perks": {
              name: "Abonnements et avantages consommateur",
              description:
                "Abonnements consommateurs et services utilitaires (hors médias)",
              examples: [
                "Amazon Prime",
                "Abonnements entrepôts (Costco, etc.)",
                "Stockage cloud ou gestionnaire de mots de passe",
                "Applications mobiles payantes de productivité (tâches, habitudes)",
              ],
            },
            "family-kids": {
              name: "Famille et enfants",
              description: "Activités familiales et garde d'enfants",
              examples: [
                "Frais de camp d'été",
                "Activités parascolaires (musique, danse, codage, art)",
                "Passes d'activités familiales (zoos, aquariums, parcs d'attractions)",
                "Garderie",
              ],
            },
          },
        },
        actions: {
          title: "Actions",
          save: "Enregistrer les modifications",
          saving: "Enregistrement...",
          cancel: "Annuler",
          delete: "Supprimer le groupe",
        },
        dialogs: {
          unsaved: {
            title: "Modifications non enregistrées",
            description:
              "Vous avez des modifications non enregistrées. Êtes-vous sûr de vouloir quitter sans enregistrer ?",
            keepEditing: "Continuer l'édition",
            discard: "Abandonner les modifications",
          },
          delete: {
            title: "Supprimer le groupe",
            description:
              "Cette action supprimera définitivement le groupe « {{name}} ». Tous les membres perdront l'accès à cet avantage.",
            confirmPrompt: "Pour confirmer, saisissez le nom du groupe :",
            cancel: "Annuler",
            confirm: "Confirmer la suppression",
          },
          final: {
            title: "Confirmation finale",
            description:
              "Êtes-vous absolument sûr de vouloir supprimer ce groupe ? Cette action est irréversible.",
            cancel: "Annuler",
            confirm: "Supprimer le groupe",
          },
        },
        toasts: {
          deleteNameMismatch:
            "Saisissez exactement le nom du groupe pour confirmer la suppression",
          updateSuccess: "Groupe mis à jour avec succès !",
          updateFailed: "Échec de la mise à jour du groupe. Veuillez réessayer.",
          deleteSuccess: "Groupe supprimé avec succès !",
          deleteFailed: "Échec de la suppression du groupe. Veuillez réessayer.",
        },
      },
      lifestyleTransactions: {
        header: "Transactions LSA",
        subheader: "Gérez et examinez les transactions de dépenses style de vie",
        breadcrumbs: {
          benefits: "Avantages",
          lifestyleAccount: "Compte style de vie",
          transactions: "Transactions",
        },
        statusFilterLabel: "Statut:",
        statusOptions: {
          all: "Tous",
          completed: "Terminé",
          pending: "En attente",
          incomplete: "Incomplet",
        },
        tableHeaders: {
          brand: "Marque",
          category: "Catégorie",
          amount: "Montant",
          date: "Date",
          status: "Statut",
          receipt: "Reçu",
          actions: "Actions",
        },
        receipt: {
          view: "Voir",
          none: "Aucun reçu",
        },
        statusTags: {
          completed: "Terminé",
          pending: "En attente",
          incomplete: "Incomplet",
          denied: "Refusé",
          unknown: "Inconnu",
        },
        loading: "Chargement des transactions...",
        loadingMore: "Chargement de transactions supplémentaires...",
        errorTitle: "Erreur lors du chargement des transactions",
        errorDescription: "Erreur lors du chargement des transactions",
        retry: "Réessayer",
        pagination: {
          showing: "Affichage de",
          to: "à",
          of: "sur",
          previous: "Précédent",
          next: "Suivant",
        },
        approveError: {
          title: "Échec de l'approbation",
          message:
            "Nous n'avons pas pu approuver cette transaction. Veuillez réessayer.",
          ok: "Ok",
        },
        actions: {
          refreshAria: "Rafraîchir les transactions",
          approve: "Approuver",
          reject: "Refuser",
        },
        toasts: {
          missingId: "Identifiant de transaction manquant. Veuillez réessayer.",
          missingPayment:
            "Aucun mode de paiement disponible pour traiter cette approbation.",
          approveSuccess: "Transaction approuvée",
          approveFailed: "Échec de l'approbation de la transaction",
          rejectSuccess: "Transaction refusée",
          rejectFailed: "Échec du refus de la transaction",
        },
        labels: {
          unknownCategory: "Catégorie inconnue",
        },
      },
      profile: {
        subheader: "Complétez ou mettez à jour les paramètres de votre organisation ici",
        companyInformation: "Informations sur l'entreprise",
        companyName: "Nom de l'entreprise",
        description: "Description",
        primaryAddress: "Adresse principale de l'entreprise",
        editCompany: "Modifier l'entreprise",
        editDescription: "Modifier la description",
        addAddress: "Ajouter une adresse",
        editAddress: "Modifier l'adresse",
        addressAlert:
          "🚨 Veuillez ajouter votre adresse, puis passer à l'écran {{subscriptionLabel}}.",
        saveChanges: "Enregistrer les modifications",
        modal: {
          companyLabel: "Fenêtre modale de l'entreprise",
          companyTitle: "Nom de l'entreprise",
          descriptionLabel: "Fenêtre modale de la description",
          descriptionTitle: "Description de l'entreprise",
          addressLabel: "Fenêtre modale de l'adresse",
          addressSubtitle: "Remplissez tous les champs ci-dessous",
        },
        fields: {
          street: "Rue",
          city: "Ville",
          country: "Pays",
          postalCode: "Code postal",
          zipCode: "Code ZIP",
        },
        errors: {
          companyRequired: "Le nom de l'entreprise est requis",
          descriptionRequired: "La description de l'entreprise est requise",
          streetRequired: "La rue est requise",
          cityRequired: "La ville est requise",
          countryRequired: "Le pays est requis",
          provinceRequired: "La province est requise",
          postalCodeRequired: "Le code postal est requis",
        },
      },
      imageUploader: {
        uploadLogo: "Téléverser le logo",
        updateLogo: "Mettre à jour le logo",
      },
      billing: {
        subheader: "Gérez votre facturation ici",
        addressAlert:
          "🚨 Commencez par aller à la page {{organizationLabel}} et ajoutez votre adresse !",
        cardsTitle: "Cartes de crédit",
        addCard: "Ajouter votre carte",
        noCards: "Aucune carte ajoutée pour le moment",
        invoicesTitle: "Factures",
        noInvoices: "Vous n'avez pas encore de factures.",
        modalTitle: "Ajouter une carte",
        registering: "Enregistrement...",
        registerCard: "Enregistrer la carte",
        cardAdded: "Carte ajoutée !",
        errors: {
          paymentFailed: "Échec du paiement",
          paymentFailedWithMessage: "Échec du paiement: {{message}}",
        },
        paymentMethod: {
          cardFallback: "Carte",
          cardEndingIn: "{{brand}} se terminant par {{lastFourDigits}}",
          expiry: "Expiration {{month}}/{{year}}",
          edit: "Modifier",
          loading: "Chargement des cartes...",
          logoAlt: "Logo {{brand}}",
        },
        invoices: {
          loading: "Chargement...",
          periodStart: "Début de la période",
          periodEnd: "Fin de la période",
          amountPaid: "Montant payé",
          receipt: "Reçu",
          status: "Statut",
          paid: "Payé",
          pending: "En attente",
          view: "Voir",
          noReceipt: "Aucun reçu",
        },
        subscription: {
          yourPlan: "Votre forfait",
          planName: {
            basic: "Forfait de base",
            enterprise: "Forfait entreprise",
          },
          planLabel: {
            basic: "De base",
            enterprise: "Entreprise",
          },
          perMonth: "/ mois par utilisateur",
          includes: "Cela comprend:",
        },
        subscriptionPoints: {
          onlineRewards: "Récompenses en ligne",
          giftCardRewards: "Récompenses par carte-cadeau",
          travelRewards: "Récompenses de voyage",
          celebrations: "Célébrations",
          airdrops: "Airdrops",
        },
      },
      onboardingProfile: {
        title: "Configurons votre profil",
        subtitle:
          "Parlez-nous un peu de votre organisation afin que nous configurions votre compte.",
      },
      onboardingAddress: {
        title: "Ajoutons votre adresse",
        subtitle:
          "Cela nous aidera à vous lancer dans votre aventure avec Parachute.",
        fields: {
          street: "Rue",
          city: "Ville",
          province: "Province",
          country: "Pays",
          postalCode: "Code postal",
        },
      },
      onboardingUploadLogo: {
        title: "Téléversez votre logo",
        subtitleLine1: "Personnalisons votre expérience.",
        subtitleLine2: "Vous pouvez aussi passer cette étape et y revenir plus tard.",
        next: "Suivant",
        skip: "Passer cette étape",
      },
      onboardingAddCard: {
        header: "Ajoutez vos informations de paiement",
        subheader:
          "Ne vous inquiétez pas - vous ne serez pas facturé tant que votre équipe ne nous aura pas rejoints.",
        addTeamMembersPrompt: "Ajoutons maintenant des membres de l'équipe...",
      },
      pendingApproval: {
        header: "Modes de paiement",
        subheader: "Gérez votre facturation ici",
        loading: "Chargement...",
      },
    },
  },
};

i18n.use(initReactI18next).init({
  resources,
  lng: getInitialLanguage(),
  fallbackLng: DEFAULT_LANGUAGE,
  supportedLngs: ["en", "fr"],
  interpolation: {
    escapeValue: false,
  },
  returnObjects: true,
});

dayjs.locale(i18n.language);
i18n.language && typeof document !== "undefined" && (document.documentElement.lang = i18n.language);
i18n.on("languageChanged", (lng) => {
  if (typeof window !== "undefined") {
    localStorage.setItem("i18nextLng", lng);
    document.documentElement.lang = lng;
  }
  dayjs.locale(lng);
});

export default i18n;
